import { useMutation, useQueryClient } from "react-query";
import {
  createCompositeProduct,
  deleteVariantFromBackend,
  editCompositeProduct,
  replicateProduct,
  uploadProductImage,
  generateOrderInvoice,
  bulkUpdateProductPrice,
  approveStockTransfer,
  rejectStockTransfer,
  updateCreditLimit,
  sendCreditLimitReminder,
  updateProductTrackItem,
  deleteProductTrackItem,
  removeShipping,
  fundStoreWallet,
  debitStoreWallet,
  toggleStoreWalletStatusMutation,
  toggleStoreWallet2fAuthMutation,
  completeDebitTnx,
  resendDebitTnxOtp,
  createStoreWallet,
  completeDebitTnxViaSellMode,
  downloadStoreWalletStatement,
  createProductUnit,
  downloadDebtorStatement,
  sendAuthorisationPin,
  createSubWallet,
  editSubWallet,
  createStoreWalletSession,
  validateStoreWalletSession,
  startReconciliations,
  approveReconciliation,
  rejectReconciliation,
  updateScheduleDelivery,
  activateScheduleDeliveryCheckout,
  downloadLedgerAccountStatement,
  bulkDeleteProductPrice,
  addMultipleProducts,
  addMultipleCompositeProducts,
  createCategory,
  deleteRestockDraft,
  approveReturn,
  rejectReturn,
  createStockRemovalRequest,
  updateStockRemovalRequestDraft,
  submitStockRemovalRequestDraft,
  approveStockRemovalRequest,
  rejectStockRemovalRequest,
  deleteStockRemovalRequest,
} from "../../../services/inventory";
import {
  addCityToLocation,
  updateReceiptCustomaizationData,
} from "../../../services/business";
import { createShipping, updateShipping } from "../../../services/customer";

import { useContext } from "react";
import { ToastContext } from "../../context";

export const useCreateCompositeProducts = () => {
  return useMutation((data) => createCompositeProduct(data));
};

export const useEditCompositeProduct = () => {
  return useMutation((data) => editCompositeProduct(data));
};

export const useImageUploadMutation = () => {
  return useMutation((data) => uploadProductImage(data));
};

export const useReplicateProduct = () => {
  return useMutation((data) => replicateProduct(data));
};

export const useDeleteVariantFromBackend = () => {
  return useMutation((data) => deleteVariantFromBackend(data));
};

export const useGenerateInvoice = () => {
  const generateInvoice = useMutation((data) => generateOrderInvoice(data));
  const triggerToast = useContext(ToastContext);

  const handlegenerateInvoice = ({
    id,
    bank_account_id,
    onSuccess,
    onSettled,
  }) => {
    generateInvoice.mutate(
      { id, bank_account_id },
      {
        onSuccess: (res) => {
          const invoiceUrl = res?.data?.data?.url;
          triggerToast(`Invoice for order ID: ${id} generated`, "success");
          if (onSuccess) {
            onSuccess({ invoiceUrl, res });
          } else {
            window.open(invoiceUrl, "_blank");
          }
        },
        onSettled: (data, error, variables, context) => {
          if (onSettled) onSettled(data, error, variables, context);
        },
      },
    );
  };
  return {
    handlegenerateInvoice,
    isLoading: generateInvoice.isLoading,
  };
};

export const useBulkUpdateProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => bulkUpdateProductPrice(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getProductList"]);
    },
  });
};

export const useApproveStockTransfer = () => {
  return useMutation((data) => approveStockTransfer(data));
};

export const useRejectStockTransfer = () => {
  return useMutation((data) => rejectStockTransfer(data));
};

export const useEditCreditLimitMutation = () => {
  return useMutation((data) => updateCreditLimit(data));
};

export const useSendCreditLimitReminderMutation = () => {
  return useMutation((data) => sendCreditLimitReminder(data));
};

export const useUpdateProductTrackItem = () => {
  return useMutation((data) => updateProductTrackItem(data));
};

export const useDeleteProductTrackItem = () => {
  return useMutation((data) => deleteProductTrackItem(data));
};

export const useCreateShippingMutation = () => {
  return useMutation((data) => createShipping(data));
};

export const useEditShippingMutation = () => {
  return useMutation((data) => updateShipping(data));
};

export const useDeleteShippingMutation = () => {
  return useMutation((data) => removeShipping(data));
};

export const useAddCityToLocation = () => {
  return useMutation((data) => addCityToLocation(data));
};

export const useFundStoreWalletMutation = () => {
  return useMutation((data) => fundStoreWallet(data));
};

export const useDebitStoreWalletMutation = () => {
  return useMutation((data) => debitStoreWallet(data));
};

export const useDownloadStatementMutation = () => {
  return useMutation((data) => downloadStoreWalletStatement(data));
};

export const useCompleteDebitTnx = (isViaSellMode = false) => {
  return useMutation((data) => {
    return isViaSellMode
      ? completeDebitTnxViaSellMode(data)
      : completeDebitTnx(data);
  });
};

export const useResendDebitTnxOtp = () => {
  return useMutation((data) => resendDebitTnxOtp(data));
};

export const useToggleStoreWalletStatusMutation = () => {
  return useMutation((data) => toggleStoreWalletStatusMutation(data));
};

export const useToggleStoreWallet2fAuthMutation = () => {
  return useMutation((data) => toggleStoreWallet2fAuthMutation(data));
};

export const useCreateStoreWalletMutation = () => {
  return useMutation((data) => createStoreWallet(data));
};

export const useCreateProductUnit = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createProductUnit(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockUnits"]);
    },
  });
};

export const useDownloadDebtorStatementMutation = () => {
  return useMutation((data) => downloadDebtorStatement(data));
};

export const useSendAuthorizationPinMutation = () => {
  return useMutation((data) => sendAuthorisationPin(data));
};

export const useCreateSubWalletMutation = () => {
  return useMutation((data) => createSubWallet(data));
};

export const useEditSubWalletMutation = () => {
  return useMutation((data) => editSubWallet(data));
};

export const useCreateStoreWalletSession = () => {
  return useMutation((data) => createStoreWalletSession(data));
};

export const useValidateWalletSession = () => {
  return useMutation((data) => validateStoreWalletSession(data));
};

export const useStartReconciliations = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => startReconciliations(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getReconciliations"]);
    },
  });
};

export const useApproveReconciliation = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => approveReconciliation(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getReconciliations"]);
    },
  });
};

export const useRejectReconciliation = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => rejectReconciliation(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getReconciliations"]);
    },
  });
};

export const useUpdateReceiptCustomaizationDataMutation = () => {
  return useMutation((data) => updateReceiptCustomaizationData(data));
};

export const useUpdateScheduleDeliveryDataMutation = () => {
  return useMutation((data) => updateScheduleDelivery(data));
};

export const useActivateScheduleDeliveryCheckoutMutation = () => {
  return useMutation((data) => activateScheduleDeliveryCheckout(data));
};

export const useDownloadLedgerAccountStatementMutation = () => {
  return useMutation((data) => downloadLedgerAccountStatement(data));
};

export const useBulkDeleteProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => bulkDeleteProductPrice(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getProductList"]);
    },
  });
};

export const useAddMultipleProducts = () => {
  return useMutation((data) => addMultipleProducts(data));
};

export const useAddMultipleCompositeProducts = () => {
  return useMutation((data) => addMultipleCompositeProducts(data));
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createCategory(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("getProductCategoryList");
    },
  });
};

export const useDeleteRestockDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => deleteRestockDraft(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("getRestock");
      queryClient.invalidateQueries("getPurchaseOrders");
    },
  });
};

export const useApproveReturn = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => approveReturn(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getReturnRequestsList"]);
      queryClient.invalidateQueries(["getReturnRequestDetails"]);
      queryClient.invalidateQueries(["getReturnsList"]);
    },
  });
};

export const useRejectReturn = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => rejectReturn(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getReturnRequestsList"]);
      queryClient.invalidateQueries(["getReturnRequestDetails"]);
      queryClient.invalidateQueries(["getReturnsList"]);
    },
  });
};

export const useCreateStockRemovalRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createStockRemovalRequest(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getProductList"]);
    },
  });
};

export const useUpdateStockRemovalRequestDraft = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => updateStockRemovalRequestDraft(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getStockRemovalRequestDetails"]);
    },
  });
};

export const useSubmitStockRemovalRequestDraft = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => submitStockRemovalRequestDraft(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getStockRemovalRequestDetails"]);
    },
  });
};

export const useApproveStockRemovalRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => approveStockRemovalRequest(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getStockRemovalRequestDetails"]);
      queryClient.invalidateQueries(["getProductList"]);
      queryClient.invalidateQueries(["getStockHistory"]);
    },
  });
};

export const useRejectStockRemovalRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => rejectStockRemovalRequest(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getStockRemovalRequestDetails"]);
    },
  });
};

export const useDeleteStockRemovalRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => deleteStockRemovalRequest(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
    },
  });
};
